import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { api, API_BASE } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  CalendarCheck, Clock, MapPin, CheckCircle2, Loader2, Mail, Phone, User,
  Fingerprint, ScanLine, Send, PartyPopper, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import { SERVICES, COMPANY } from "../data/content";

const SERVICE_ICONS = {
  "Live Scan Digital Fingerprinting": ScanLine,
  "Traditional Ink Fingerprint Cards": Fingerprint,
  "FDLE Digital Submissions": Send,
};

export const BookingSection = () => {
  const [service, setService] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [closedReason, setClosedReason] = useState("");
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Listen for service selection from service cards
  useEffect(() => {
    const handler = (e) => {
      setService(e.detail);
      setConfirmation(null);
    };
    window.addEventListener("cfs:select-service", handler);
    return () => window.removeEventListener("cfs:select-service", handler);
  }, []);

  const fetchSlots = useCallback(async (d) => {
    setSlotsLoading(true);
    setClosedReason("");
    setSlots([]);
    try {
      const dateStr = format(d, "yyyy-MM-dd");
      const res = await api.get(`/availability`, { params: { date: dateStr } });
      if (!res.data.open) {
        setClosedReason(res.data.reason || "No availability on this date.");
      } else {
        setSlots(res.data.slots);
      }
    } catch (err) {
      toast.error("Could not load availability. Please try again.");
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  const handleDateSelect = (d) => {
    setDate(d);
    setSelectedTime(null);
    if (d) fetchSlots(d);
  };

  const update = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const validate = () => {
    const errs = {};
    if (!service) errs.service = "Please select a service.";
    if (!date) errs.date = "Please select a date.";
    if (!selectedTime) errs.time = "Please select a time slot.";
    if (!form.name || form.name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    if (!form.phone || form.phone.replace(/\D/g, "").length < 5) errs.phone = "Please enter a valid phone number.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/appointments", {
        service,
        date: format(date, "yyyy-MM-dd"),
        time: selectedTime,
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      });
      setConfirmation(res.data);
      toast.success("Appointment booked successfully!");
    } catch (err) {
      const detail = err.response?.data?.detail || "Booking failed. Please try again.";
      toast.error(detail);
      if (err.response?.status === 409 && date) {
        fetchSlots(date); // refresh capacity
        setSelectedTime(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetBooking = () => {
    setConfirmation(null);
    setService(null);
    setDate(null);
    setSlots([]);
    setSelectedTime(null);
    setForm({ name: "", email: "", phone: "", notes: "" });
    setErrors({});
  };

  const disabledDays = [{ before: new Date() }, { dayOfWeek: [0, 6] }];

  if (confirmation) {
    const a = confirmation.appointment;
    return (
      <section id="booking" className="section-anchor py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
            <Card className="border-2 border-green-200 bg-white shadow-lg" data-testid="booking-success-confirmation">
              <CardContent className="p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
                    <PartyPopper className="h-7 w-7 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-[color:var(--brand-navy)]">You're Booked!</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {confirmation.email_sent
                        ? "A confirmation email has been sent to your inbox."
                        : "Please save your confirmation number below."}
                    </p>
                  </div>
                </div>

                <div className="bg-secondary rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-sm text-muted-foreground">Confirmation Number</span>
                    <span className="font-mono-slot font-semibold text-lg text-[color:var(--brand-navy)]" data-testid="booking-confirmation-number">
                      {a.confirmation_number}
                    </span>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Service</p>
                      <p className="font-medium text-[color:var(--brand-navy)] mt-0.5">{a.service}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Date &amp; Time</p>
                      <p className="font-medium text-[color:var(--brand-navy)] mt-0.5">{a.date_display} at {a.time_display}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p className="font-medium text-[color:var(--brand-navy)] mt-0.5">{a.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Location</p>
                      <p className="font-medium text-[color:var(--brand-navy)] mt-0.5">{COMPANY.address}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-[color:var(--brand-navy)]">
                  <strong>What to bring:</strong> A valid government-issued photo ID and any fingerprint cards or agency
                  paperwork (including ORI numbers) required for your submission.
                </div>

                <div className="mt-6">
                  <p className="text-sm font-medium text-[color:var(--brand-navy)] mb-3">Add this appointment to your calendar:</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      className="border-[color:var(--brand-sky)] text-[color:var(--brand-sky)] hover:bg-blue-50 hover:text-[color:var(--brand-sky)] btn-lift"
                      asChild
                      data-testid="booking-add-google-calendar-button"
                    >
                      <a href={confirmation.google_calendar_link} target="_blank" rel="noopener noreferrer">
                        <CalendarPlus className="h-4 w-4 mr-2" /> Add to Google Calendar
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      className="btn-lift"
                      asChild
                      data-testid="booking-download-ics-button"
                    >
                      <a href={`${API_BASE}/appointments/${a.id}/calendar.ics`} download>
                        <CalendarCheck className="h-4 w-4 mr-2" /> Apple / Outlook (.ics)
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={resetBooking}
                    className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] btn-lift"
                    data-testid="booking-book-another-button"
                  >
                    <CalendarPlus className="h-4 w-4 mr-2" /> Book Another Appointment
                  </Button>
                  <Button variant="outline" asChild className="btn-lift">
                    <a href={COMPANY.phoneHref}>
                      <Phone className="h-4 w-4 mr-2" /> Questions? Call {COMPANY.phone}
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="section-anchor py-16 sm:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--brand-navy)]">
            Schedule Your Appointment
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Choose a service, pick a date and time, and you're all set. Appointments are 30 minutes, Monday through
            Friday, 9:00 AM &ndash; 3:00 PM.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: steps */}
          <div className="lg:col-span-8 space-y-6">
            {/* Step 1: Service */}
            <Card className="border bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="h-7 w-7 rounded-full bg-[color:var(--brand-navy)] text-white text-sm font-semibold flex items-center justify-center">1</span>
                  <h3 className="font-heading font-semibold text-[color:var(--brand-navy)]">Select a Service</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="booking-service-select">
                  {SERVICES.map((s) => {
                    const Icon = SERVICE_ICONS[s.apiValue];
                    const active = service === s.apiValue;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setService(s.apiValue)}
                        className={`slot-button text-left p-4 rounded-xl border-2 min-h-[44px] ${
                          active
                            ? "border-[color:var(--brand-sky)] bg-blue-50/50 ring-2 ring-[hsl(var(--ring))]"
                            : "border-border bg-white hover:bg-[color:var(--brand-sand)]"
                        }`}
                        data-testid={`booking-service-option-${s.id}`}
                      >
                        <Icon className={`h-5 w-5 mb-2 ${active ? "text-[color:var(--brand-sky)]" : "text-[color:var(--brand-navy)]"}`} />
                        <span className="block text-sm font-medium leading-snug text-[color:var(--brand-navy)]">{s.title}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.service && <p className="text-xs text-destructive mt-2" aria-live="polite">{errors.service}</p>}
              </CardContent>
            </Card>

            {/* Step 2: Date & Time */}
            <Card className="border bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="h-7 w-7 rounded-full bg-[color:var(--brand-navy)] text-white text-sm font-semibold flex items-center justify-center">2</span>
                  <h3 className="font-heading font-semibold text-[color:var(--brand-navy)]">Pick a Date &amp; Time</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div data-testid="booking-date-picker">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      disabled={disabledDays}
                      className="rounded-xl border w-fit mx-auto md:mx-0"
                      data-testid="booking-calendar"
                    />
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> Open Monday&ndash;Friday. Weekends unavailable.
                    </p>
                    {errors.date && <p className="text-xs text-destructive mt-1" aria-live="polite">{errors.date}</p>}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[color:var(--brand-navy)] mb-3">
                      {date ? `Available times for ${format(date, "EEEE, MMMM d")}` : "Select a date to see available times"}
                    </p>
                    {slotsLoading ? (
                      <div className="flex items-center justify-center h-40 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading availability...
                      </div>
                    ) : closedReason ? (
                      <div className="p-4 bg-secondary rounded-lg text-sm text-muted-foreground" data-testid="booking-closed-message">
                        {closedReason}
                      </div>
                    ) : slots.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {slots.map((slot) => {
                          const isSelected = selectedTime === slot.time;
                          const disabled = !slot.available;
                          return (
                            <button
                              key={slot.time}
                              disabled={disabled}
                              onClick={() => setSelectedTime(slot.time)}
                              className={`slot-button relative rounded-lg border p-2.5 min-h-[52px] text-sm font-mono-slot ${
                                disabled
                                  ? "opacity-45 cursor-not-allowed bg-muted border-border"
                                  : isSelected
                                  ? "border-[color:var(--brand-sky)] bg-blue-50/60 ring-2 ring-[hsl(var(--ring))] font-semibold"
                                  : "bg-white border-border hover:bg-[color:var(--brand-sand)]"
                              }`}
                              data-testid={`booking-time-slot-${slot.time.replace(":", "")}`}
                              title={disabled ? (slot.past ? "This time has passed" : "This time is fully booked") : `${slot.remaining} of 3 spots left`}
                            >
                              <span className="block text-[color:var(--brand-navy)]">{slot.time_display}</span>
                              <Badge
                                variant="secondary"
                                className={`mt-1 text-[10px] px-1.5 py-0 ${slot.remaining === 0 ? "text-muted-foreground" : "text-[color:var(--brand-navy)]"}`}
                              >
                                {slot.past ? "Passed" : slot.remaining === 0 ? "Full" : `${slot.remaining}/3 left`}
                              </Badge>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-40 border border-dashed rounded-lg text-sm text-muted-foreground">
                        <CalendarCheck className="h-4 w-4 mr-2" /> Pick a weekday on the calendar
                      </div>
                    )}
                    {errors.time && <p className="text-xs text-destructive mt-2" aria-live="polite">{errors.time}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 3: Details */}
            <Card className="border bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="h-7 w-7 rounded-full bg-[color:var(--brand-navy)] text-white text-sm font-semibold flex items-center justify-center">3</span>
                  <h3 className="font-heading font-semibold text-[color:var(--brand-navy)]">Your Details</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="booking-details-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="booking-name">Full Name *</Label>
                    <Input id="booking-name" placeholder="Your full name" value={form.name} onChange={update("name")} data-testid="booking-name-input" />
                    {errors.name && <p className="text-xs text-destructive" aria-live="polite">{errors.name}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="booking-email">Email Address *</Label>
                    <Input id="booking-email" type="email" placeholder="you@example.com" value={form.email} onChange={update("email")} data-testid="booking-email-input" />
                    {errors.email && <p className="text-xs text-destructive" aria-live="polite">{errors.email}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="booking-phone">Phone Number *</Label>
                    <Input id="booking-phone" type="tel" placeholder="345-XXX-XXXX" value={form.phone} onChange={update("phone")} data-testid="booking-phone-input" />
                    {errors.phone && <p className="text-xs text-destructive" aria-live="polite">{errors.phone}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="booking-notes">Notes (optional)</Label>
                    <Textarea id="booking-notes" placeholder="Anything we should know? (e.g., agency, ORI number)" rows={1} value={form.notes} onChange={update("notes")} data-testid="booking-notes-input" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: sticky summary */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <Card className="border-2 border-[color:var(--brand-navy)] bg-white shadow-md">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-[color:var(--brand-navy)] mb-5">Booking Summary</h3>
                  <div className="space-y-4 text-sm" data-testid="booking-summary">
                    <div className="flex items-start gap-3">
                      <Fingerprint className="h-4.5 w-4.5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" style={{ height: 18, width: 18 }} />
                      <div>
                        <p className="text-muted-foreground text-xs">Service</p>
                        <p className="font-medium text-[color:var(--brand-navy)]">{service || "Not selected"}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CalendarCheck className="h-4.5 w-4.5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" style={{ height: 18, width: 18 }} />
                      <div>
                        <p className="text-muted-foreground text-xs">Date</p>
                        <p className="font-medium text-[color:var(--brand-navy)]">{date ? format(date, "EEEE, MMMM d, yyyy") : "Not selected"}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="h-4.5 w-4.5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" style={{ height: 18, width: 18 }} />
                      <div>
                        <p className="text-muted-foreground text-xs">Time</p>
                        <p className="font-medium text-[color:var(--brand-navy)]">
                          {selectedTime ? slots.find((s) => s.time === selectedTime)?.time_display : "Not selected"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4.5 w-4.5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" style={{ height: 18, width: 18 }} />
                      <div>
                        <p className="text-muted-foreground text-xs">Location</p>
                        <p className="font-medium text-[color:var(--brand-navy)] leading-snug">SIGMA Building, 1st Floor, 93 Hospital Rd., George Town</p>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-5" />

                  <Button
                    className="w-full h-12 bg-[color:var(--brand-sky)] hover:bg-[#1791c5] text-white font-semibold btn-lift"
                    disabled={!service || !date || !selectedTime || submitting}
                    onClick={handleSubmit}
                    data-testid="booking-confirm-appointment-button"
                  >
                    {submitting ? (
                      <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Booking...</span>
                    ) : (
                      <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> Confirm Appointment</span>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    Free to book. 30-minute appointment. You'll receive an email confirmation.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
